import { brand } from "../../../../../../config/theme";
import { useState, useMemo, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import { useLicensesStore } from "../../../../../../store/reseller-admin/licensesStore";
import type { License } from "../../../../../../store/reseller-admin/licensesStore";
import { useLicensesList } from "../../../../../../features/reseller/queries";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import type { AlertState } from "../types";

export interface LicenseFormData {
  name: string;
  description: string;
  status: "active" | "inactive" | "draft";
  billing_model: "per_unit" | "flat" | "tiered";
  price_per_unit: number;
  flat_price: number;
  currency: "MXN" | "USD";
  billing_cycle: "monthly" | "quarterly" | "yearly";
  apps: string[];
  features: string[];
  entity_limits: {
    vehicles: number | null;
    devices: number | null;
    users: number | null;
    geofences: number | null;
    drivers: number | null;
    groups: number | null;
    alerts: number | null;
    reports: number | null;
  };
  notes: string;
}

const defaultFormValues: LicenseFormData = {
  name: "",
  description: "",
  status: "draft",
  billing_model: "per_unit",
  price_per_unit: 0,
  flat_price: 0,
  currency: "MXN",
  billing_cycle: "monthly",
  apps: [],
  features: [],
  entity_limits: {
    vehicles: null,
    devices: null,
    users: null,
    geofences: null,
    drivers: null,
    groups: null,
    alerts: null,
    reports: null,
  },
  notes: "",
};

interface FilterParams {
  page: number;
  limit: number;
  status?: string;
  name?: string;
  billing_model?: string;
}

export function useLicenseForm() {
  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<"details" | "edit" | "add">("details");
  const [alert, setAlert] = useState<AlertState>({
    show: false,
    title: "",
    message: "",
    type: "info",
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editLicenseId, setEditLicenseId] = useState<string | null>(null);
  const [originalLicenseData, setOriginalLicenseData] = useState<License | null>(null);

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("grid");
  const itemsPerPage = 8;
  const debouncedSearch = useDebounce(searchQuery);

  const {
    fetchLicense,
    createLicense,
    updateLicense,
    deleteLicense,
  } = useLicensesStore();

  const form = useForm<LicenseFormData>({
    defaultValues: defaultFormValues,
  });

  const filterParams = useMemo(
    (): FilterParams => ({
      page: currentPage,
      limit: itemsPerPage,
      status: selectedStatus !== "all" ? selectedStatus : undefined,
      name: debouncedSearch || undefined,
    }),
    [currentPage, itemsPerPage, selectedStatus, debouncedSearch],
  );

  const licensesQuery = useLicensesList(filterParams);
  const licenses = licensesQuery.data?.data ?? [];
  const meta = licensesQuery.data?.meta;
  const loading = licensesQuery.isLoading;

  const queryClient = useQueryClient();
  const invalidateLicenses = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["resellerLicenses"] });
  }, [queryClient]);

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleDetailsClick = (license: License) => {
    setSelectedLicense(license);
    setModalType("details");
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleEditClick = async (license: License) => {
    try {
      const fullLicense = await fetchLicense(license.id);
      const licenseData = (fullLicense as { data?: License })?.data ?? fullLicense;

      const resetData: LicenseFormData = {
        name: licenseData.name || "",
        description: licenseData.description || "",
        status: licenseData.status || "draft",
        billing_model: licenseData.billing_model || "per_unit",
        price_per_unit: licenseData.price_per_unit ?? 0,
        flat_price: licenseData.flat_price ?? 0,
        currency: licenseData.currency || "MXN",
        billing_cycle: licenseData.billing_cycle || "monthly",
        apps: licenseData.apps || [],
        features: licenseData.features || [],
        entity_limits: {
          vehicles: licenseData.entity_limits?.vehicles ?? null,
          devices: licenseData.entity_limits?.devices ?? null,
          users: licenseData.entity_limits?.users ?? null,
          geofences: licenseData.entity_limits?.geofences ?? null,
          drivers: licenseData.entity_limits?.drivers ?? null,
          groups: licenseData.entity_limits?.groups ?? null,
          alerts: licenseData.entity_limits?.alerts ?? null,
          reports: licenseData.entity_limits?.reports ?? null,
        },
        notes: licenseData.notes || "",
      };

      form.reset(resetData);
      setOriginalLicenseData(licenseData);
      setEditLicenseId(license.id);
      setIsEdit(true);
      setShowAddModal(true);
      document.body.style.overflow = "hidden";
    } catch (error) {
      console.error("Error al cargar licencia:", error);
      await Swal.fire({
        title: "Error",
        text: "No se pudo cargar la licencia",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLicense(null);
    form.reset(defaultFormValues);
    document.body.style.overflow = "";
  };

  const closeAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setEditLicenseId(null);
    setOriginalLicenseData(null);
    form.reset(defaultFormValues);
    setShowAddModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setEditLicenseId(null);
    setOriginalLicenseData(null);
    setLoadingSubmit(false);
    form.reset(defaultFormValues);
    document.body.style.overflow = "";
  };

  const hasFormChanges = () => {
    if (!isEdit || !originalLicenseData) return false;
    const currentValues = form.getValues();
    return JSON.stringify(currentValues) !== JSON.stringify({
      name: originalLicenseData.name || "",
      description: originalLicenseData.description || "",
      status: originalLicenseData.status || "draft",
      billing_model: originalLicenseData.billing_model || "per_unit",
      price_per_unit: originalLicenseData.price_per_unit ?? 0,
      flat_price: originalLicenseData.flat_price ?? 0,
      currency: originalLicenseData.currency || "MXN",
      billing_cycle: originalLicenseData.billing_cycle || "monthly",
      apps: originalLicenseData.apps || [],
      features: originalLicenseData.features || [],
      entity_limits: originalLicenseData.entity_limits || defaultFormValues.entity_limits,
      notes: originalLicenseData.notes || "",
    });
  };

  const handleDeleteLicense = async (license: License) => {
    const result = await Swal.fire({
      title: "¿Desactivar licencia?",
      text: `¿Realmente desea desactivar la licencia "${license.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteLicense(license.id);
      await Swal.fire({
        title: "¡Desactivación exitosa!",
        text: `La licencia "${license.name}" ha sido desactivada correctamente`,
        icon: "success",
        confirmButtonText: "Aceptar",
        confirmButtonColor: brand.primary,
      });
      invalidateLicenses();
      closeModal();
      closeAddModal();
    } catch (error) {
      console.error("Error al desactivar licencia:", error);
      await Swal.fire({
        title: "Error",
        text: "No se pudo desactivar la licencia",
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ef4444",
      });
    }
  };

  const handleSubmitLicense = async (data: LicenseFormData) => {
    setLoadingSubmit(true);
    try {
      const licenseData = {
        name: data.name,
        description: data.description || undefined,
        status: data.status,
        billing_model: data.billing_model,
        price_per_unit: data.billing_model === "per_unit" ? data.price_per_unit : undefined,
        flat_price: data.billing_model === "flat" ? data.flat_price : undefined,
        currency: data.currency,
        billing_cycle: data.billing_cycle,
        apps: data.apps,
        features: data.features.filter((f) => f.trim() !== ""),
        entity_limits: data.entity_limits,
        notes: data.notes || undefined,
      };

      if (isEdit && editLicenseId) {
        await updateLicense(editLicenseId, licenseData);
        await Swal.fire({
          title: "¡Éxito!",
          text: "Licencia actualizada correctamente",
          icon: "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: brand.primary,
        });
      } else {
        await createLicense(licenseData);
        await Swal.fire({
          title: "¡Éxito!",
          text: "Licencia creada correctamente",
          icon: "success",
          confirmButtonText: "Aceptar",
          confirmButtonColor: brand.primary,
        });
      }

      closeAddModal();
      form.reset(defaultFormValues);
      invalidateLicenses();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      console.error("Error al guardar licencia:", error);
      await Swal.fire({
        title: "Error",
        text:
          err?.response?.data?.message ||
          err?.message ||
          (isEdit ? "Error al actualizar la licencia" : "Error al crear la licencia"),
        icon: "error",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ef4444",
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  return {
    // Data
    licenses,
    meta,
    loading,
    selectedLicense,
    alert,

    // Modal states
    isModalOpen,
    modalType,
    showAddModal,
    loadingSubmit,
    isEdit,
    editLicenseId,

    // Form
    form,

    // Filter states
    selectedStatus,
    searchQuery,
    currentPage,
    selectedView,
    itemsPerPage,

    // Handlers
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleEditClick,
    handleAddClick,
    handleSubmitLicense,
    setCurrentPage,
    setSelectedView,
    closeModal,
    closeAddModal,
    closeAlert,
    hasFormChanges,
    handleDeleteLicense,
  };
}
